// © Microsoft Corporation. All rights reserved.

import { AppType, ConnectorNamespaces } from "@msnews/experiences-constants";
import { ChildExperienceConfigInfo, SelectedPivotStorageId, appEnvironment, getLocalStorage } from "@msnews/core";
import { PeregrineRoute, PivotSelectionInfo } from "./Interfaces";
import { enableLocaleFeedPrefix, getRoutePath, getRoutePathFromExternalUrl, updateEnableLocaleFeedPrefix } from "./Utilities";

import DynamicRouteProvider from "./DynamicRouteProvider";
import PeregrineHistory from "./PeregrineHistory";
import { PeregrineRouterConfig } from "./PeregrineRouterConfig";
import { location } from "react-router-dom";

/**
 * Peregrine Router class
 */
export class PeregrineRouter {
    /** Static routes - routes that do not change based on user followed interests */
    private staticRoutes: PeregrineRoute[] = [];

    /** Dynamic route paths - routes that change based on user followed interests */
    private dynamicRoutes: PeregrineRoute[] = [];

    /** Callbacks for when dynamic routes are updated */
    private onUpdateRoutesCallbacks: ((newRoutes: PeregrineRoute[]) => void)[] = [];

    /** Peregrine router config */
    private config: PeregrineRouterConfig;

    private switchListeners: any[] = [];

    /**
     * Constructor for Peregrine Router
     * @param config
     */
    public constructor(config?: PeregrineRouterConfig) {
        if (config) {
            updateEnableLocaleFeedPrefix(config.enableLocaleFeedPrefix);
            this.setRoutes(config);
        }
        PeregrineHistory.listen(this.callSwitchListeners);

        // TODO - remove once Windows HP web component migration is complete. This is here to continue enabling client-side routing
        // in windows HP (desktopfeed) but without taking a dependency on react router
        if (appEnvironment.AppType === AppType.Windows) {
            const pushState = window.history.pushState;
            window.history.pushState = (state: any, title?: string, url?: string) => {
                pushState.apply(window.history, [state, title, url]);
                const event = new CustomEvent("pushstate", { detail: { state, title, url } });
                window.dispatchEvent(event);
            };
            window.addEventListener("pushstate", (e: CustomEvent) => {
                if (e.detail && e.detail.state && e.detail.state.shouldPush) {
                    PeregrineHistory.replace(window.location.pathname, {context: e.detail.state}, window.location.search);
                }
            });
        }
    }

    /**
     * Helper function to set routes
     */
    public setRoutes(config: PeregrineRouterConfig): void {
        this.config = config;
        this.setStaticRoutes();
        if (!this.config.disableLoadingDynamicRoutes) {
            this.setDynamicRoutes();
        }
    }

    /**
     * Switch to specific route based on canonical name, topic id (optional), and query params (optional)
     * @param canonicalName canonical name for topic
     * @param id topic id
     * @param queryParams query params to be added to the URL
     */
    public pushHistory(canonicalName: string, id?: string, context?: any, queryParams?: string): void {
        const previousPath = PeregrineHistory.location.pathname;
        PeregrineHistory.push(
            getRoutePath(canonicalName, id, true, context && context.excludeFeedPrefix),
            {
                previousPath: previousPath,
                context: context
            },
            queryParams);
    }

    /**
     * Get static routes
     */
    public getStaticRoutes(): PeregrineRoute[] {
        return this.staticRoutes;
    }

    /**
     * Get dynamic routes and set dynamic route callback for when dynamic routes are updated
     * @param onUpdateDynamicRoutesCallback Dynamic routes update callback
     */
    public getDynamicRoutes(): PeregrineRoute[] {
        return this.dynamicRoutes;
    }

    /**
     * Returns dynamic (topic) route path
     */
    public getDynamicRoute(): PeregrineRoute {
        // Get display from the pathname, if available it will be the url segment right after "interest"
        const splitPath = PeregrineHistory.location.pathname.split("/");
        const interestIndex = splitPath.findIndex(x => x.toLowerCase() === "interest");
        const display = interestIndex > -1 && interestIndex < splitPath.length - 1 ? splitPath[interestIndex + 1] : "";

        return {
            path: getRoutePath("interest"),
            experienceInfo: this.config.dynamicRouteExperienceInfo as ChildExperienceConfigInfo,
            pivotId: "oneriver.xfeed",
            display: decodeURIComponent(display)
        };
    }

    public isDynamicRoute(path: string): boolean {
        let prefix = "";
        if (enableLocaleFeedPrefix) {
            prefix = appEnvironment.AppType === AppType.Windows ? `/${appEnvironment.CurrentMarket}` + "/feed" : `/${appEnvironment.CurrentMarket}`;
        }
        prefix += "/interest";
        return path.indexOf(prefix) === 0;
    }

    /**
     * Returns the current route path
     */
    public getCurrentRoute(): PeregrineRoute {
        const currentPath = PeregrineHistory.location.pathname;
        const currentRoute = this.staticRoutes.find(route => route.path === currentPath);
        if (currentRoute) {
            return currentRoute;
        }

        if (/\/interest\/.+\//.test(currentPath)) {
            return this.getDynamicRoute();
        }

        return null;
    }

    public addDynamicRouteListener(onUpdateDynamicRoutesCallback: (newRoutes: PeregrineRoute[]) => void): void {
        this.onUpdateRoutesCallbacks.push(onUpdateDynamicRoutesCallback);
    }

    /**
     * Adds a custom switch listener for when the route changes. Uses the same pattern that the pivot manager switch listeners use
     * to help reduce code churn where switch listeners are being used
     * @param switchListener switch listener
     * @param order order in which the switch listener should be executed
     */
    public addSwitchListener(switchListener: any, order: SwitchCallbackOrder = SwitchCallbackOrder.AfterLoadContent): void {
        this.switchListeners.push({
            callback: switchListener,
            order: order
        });
    }

    /**
     * If enabled in config, get the selectedPivotId from local storage and switch to that pivot (route)
     */
    public getRememberedPivot(): void {
        // Only continue if remembered pivots are enabled
        if (!this.config.enableRememberedPivots) {
            return;
        }

        const pivotId = getLocalStorage().getItem(SelectedPivotStorageId);
        if (!pivotId) {
            return;
        }

        this.config.staticRoutes.forEach(route => {
            if (route.pivotId !== pivotId) {
                return;
            }

            this.pushHistory(route.canonicalName);
        });
    }

    /**
     * Calls all added switch listeners
     * @param location location object
     */
    private callSwitchListeners = (location: location): void => {
        if (!this.switchListeners.length) {
            return;
        }

        // set up the pivot switch info
        // using the pivot manager's pivotSwitchInfo object to reduce code churn on existing experiences
        const oldSelection = this.getPivotSelectionInfoFromPath(location.state && location.state.previousPath);
        const newSelection = this.getPivotSelectionInfoFromPath(location.pathname, location);

        const pivotSwitchInfo = {
            newSelection: newSelection,
            oldSelection: oldSelection
        };

        // Store to local storage only if enabled in config
        if (this.config.enableRememberedPivots) {
            getLocalStorage().setItem(SelectedPivotStorageId, newSelection && newSelection.pivotId);
        }

        // Order the switch listeners and call each callback
        this.switchListeners.sort((a, b) => {
            return a.order - b.order;
        });
        for (const switchListener of this.switchListeners) {
            switchListener.callback(pivotSwitchInfo);
        }
    };

    /**
     * Formats route info as a pivot selection info object
     * @param path route path
     */
    private getPivotSelectionInfoFromPath(path: string, location?: location): PivotSelectionInfo {
        if (!path) {
            return null;
        }

        const routeConfig = this.config.staticRoutes.find(staticRoute => getRoutePath(staticRoute.canonicalName, undefined, true, staticRoute.context && staticRoute.context.excludeFeedPrefix) === path);
        if (routeConfig) {
            return {
                pivotId: routeConfig.pivotId,
                telemetryPageName: routeConfig.telemetryPageName,
                telemetryPivotCategory: routeConfig.telemetryPivotCategory,
                display: routeConfig.canonicalName,
                categoryKey: routeConfig.categoryKey,
                subCategoryKey: routeConfig.subCategoryKey,
                pageTitle: routeConfig.pageTitle
            };
        }

        if (!this.isDynamicRoute(path)) {
            return null;
        }

        // We will try to get the display and pivotId from location context. Otherwise, we will get from path segments
        const splitPath = path.split("/");

        let prefixIndex = 0;
        if (enableLocaleFeedPrefix) {
            if (appEnvironment.AppType === AppType.Windows) {
                prefixIndex = 2;
            } else {
                prefixIndex = 1;
            }
        }

        const useLocation = location && location.state && location.state.context;
        const pivotId = useLocation && location.state.context.feedId ||
            splitPath.length >= 4 + prefixIndex && splitPath[3 + prefixIndex];
        const display = useLocation && (location.state.context.display || location.state.context.query) ||
            splitPath.length >= 3 + prefixIndex && decodeURIComponent(splitPath[2 + prefixIndex]);

        return {
            telemetryPageName: "xfeed",
            telemetryPivotCategory: "news",
            pivotId,
            display
        };
    }

    /**
     * Helper function to set static routes using config
     */
    private setStaticRoutes(): void {
        const { staticRoutes } = this.config;

        staticRoutes.forEach(staticRouteConfig => {

            let path: string;
            if (staticRouteConfig.canonicalName) {
                path = getRoutePath(staticRouteConfig.canonicalName, undefined, true, staticRouteConfig.context && staticRouteConfig.context.excludeFeedPrefix);
            } else {
                path = getRoutePathFromExternalUrl(staticRouteConfig.externalUrl);
            }

            const staticRoute: PeregrineRoute = {
                path,
                externalUrl: staticRouteConfig.externalUrl,
                experienceInfo: staticRouteConfig.experienceInfo as ChildExperienceConfigInfo,
                queryParamOverrides: staticRouteConfig.queryParamOverrides,
                display: staticRouteConfig.display || staticRouteConfig.canonicalName,
                pivotId: staticRouteConfig.pivotId,
                context: staticRouteConfig.context,
                categoryKey: staticRouteConfig.categoryKey,
                subCategoryKey: staticRouteConfig.subCategoryKey,
                pageTitle: staticRouteConfig.pageTitle
            };

            this.staticRoutes.push(staticRoute);
        });
    }

    /**
     * Helper function to set dynamic routes
     */
    private async setDynamicRoutes(): Promise<void> {
        const dynamicRouteProvider = new DynamicRouteProvider(this.onUpdateDynamicRoute.bind(this), this.config.dynamicRouteExperienceInfo as ChildExperienceConfigInfo);
        await dynamicRouteProvider.getDynamicPivotsFromDataConnector(this.config.dynamicRouteDataConnector as ConnectorNamespaces);
    }

    /**
     * Helper function to respond to updated dynamic routes
     */
    private onUpdateDynamicRoute(dynamicRoutes: PeregrineRoute[]): void {
        this.dynamicRoutes = dynamicRoutes;
        for (const callback of this.onUpdateRoutesCallbacks) {
            callback(this.dynamicRoutes);
        }
    }
}

/**
 * Order in which the switch listeners should be called
 */
export enum SwitchCallbackOrder {
    Telemetry = 0,
    Monetization,
    LoadContent,
    AfterLoadContent
}

let _peregrineRouter: PeregrineRouter;

export const peregrineRouter = (config?: PeregrineRouterConfig) => {
    if (!_peregrineRouter) {
        _peregrineRouter = new PeregrineRouter(config);
        return _peregrineRouter;
    }

    if (config) {
        _peregrineRouter.setRoutes(config);
    }
    return _peregrineRouter;
};
